import { format as formatDateFns } from 'date-fns'
import type { ComponentType, ReactNode } from 'react'

import { Caption1 } from '@ui/Typography'
import { TextColorType } from '@ui/design-system/colors/colors'

import { getCurrencySymbol } from '@utils/transaction.utils'

import { CurrencyType } from '@model/WalletDto'

import type {
  DxTradeExecutableSide,
  DxTradeOrder,
  DxTradeSide,
} from '../../../../model/DxTrade.types'
import { DXTRADE_POSITION_EFFECT } from '../../utils/constants'

export const buildTradeWidgetUrl = (
  symbol: string,
  side: DxTradeExecutableSide,
  tab: 'order' | 'trades' | 'chart' = 'trades'
) => `/dashboard/trade/markets?symbol=${symbol}&tab=${tab}&side=${side}&widgetType=order`

export function renderCellValue(value: unknown, simbols?: number): string | number {
  if (typeof value === 'number') return simbols !== undefined ? value.toFixed(simbols) : value
  if (typeof value === 'string' && value.trim() !== '') return value
  return '-'
}

const FX_DESCRIPTION_PATTERN = /\s+vs\s+/i

export function formatSymbol(value: unknown, description?: string): string {
  if (typeof value !== 'string' || value.trim() === '') return '-'
  const raw = value.trim()
  if (raw.includes('/')) return raw

  const hasVsInDescription =
    typeof description === 'string' && FX_DESCRIPTION_PATTERN.test(description)

  if (hasVsInDescription && raw.length >= 6) {
    return `${raw.slice(0, 3)}/${raw.slice(3, 6)}`
  }
  return raw
}

export function formatDate(iso?: string): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '-'
  return formatDateFns(d, 'dd.MM.yyyy')
}

export function formatDateTime(iso?: string): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '-'
  return formatDateFns(d, 'd MMM yyyy, HH:mm')
}

export type TypographyLike = ComponentType<{
  color?: TextColorType
  className?: string
  children?: ReactNode
}>

export function renderSide(
  side: DxTradeSide | unknown,
  TypographyComp: TypographyLike = Caption1
): ReactNode {
  if (side === 'BUY') {
    return <TypographyComp color='text-success-high-em'>Buy</TypographyComp>
  }
  if (side === 'SELL') {
    return <TypographyComp color='text-danger-high-em'>Sell</TypographyComp>
  }
  return renderCellValue(side)
}

export function getOpenLegTargetPrice(order: DxTradeOrder): number | undefined {
  const leg = (order.legs ?? []).find((l) => l.positionEffect === DXTRADE_POSITION_EFFECT.OPEN)
  return leg?.price
}

export function upperCaseFirstLetter(type?: string | null): string | undefined {
  if (typeof type !== 'string') return undefined
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
}

export function formatSignedUsd(value?: number | null, currency?: CurrencyType): string {
  if (typeof value !== 'number') return ''
  const sign = value > 0 ? '+' : value < 0 ? '-' : ''
  const abs = Math.abs(value).toFixed(2)
  if (!currency) return `${sign}${abs}`
  return `${sign}${getCurrencySymbol(currency)}${abs}`
}

export const getPnlColor = (pnl?: number | null): TextColorType => {
  if (pnl == null || pnl === 0) return 'text-high-em'
  return pnl > 0 ? 'text-success-high-em' : 'text-danger-high-em'
}

export function renderPnl(
  pnl?: number | null,
  TypographyComp: TypographyLike = Caption1,
  currency?: CurrencyType,
  formatMoney?: (value?: number, currency?: CurrencyType) => string,
  className?: string
): ReactNode {
  const sign = pnl != null && pnl > 0 ? '+' : pnl != null && pnl < 0 ? '-' : ''
  const formatted = formatMoney
    ? sign + formatMoney(pnl != null ? Math.abs(pnl) : undefined, currency)
    : formatSignedUsd(pnl, currency as CurrencyType)
  return (
    <TypographyComp color={getPnlColor(pnl)} className={className}>
      {formatted}
    </TypographyComp>
  )
}
export function formatCurrency(
  value?: number | null,
  currency?: string,
  symbols?: number
): string | number {
  if (typeof value !== 'number') return '-'
  if (!currency) return symbols ? value.toFixed(symbols) : value
  return `${getCurrencySymbol(currency as CurrencyType)}${symbols ? value.toFixed(symbols) : value}`
}
